import React, { useState, useRef, ReactNode, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";

import { Pane } from "src/app/components/Pane/Pane";
import { type ProjectAction } from "./types";
import { useShowPane } from "src/hooks/useShowPane";
import {
  CreateNewProjectPaneHeader,
  CreateNewProjectPaneModals,
  CreateNewProjectPaneFooter,
  BriefSection,
  GeneralSection,
  RequiredSection,
  WizardFieldsSection,
  SettingsSection,
  StructureSection,
} from "./components";
import useCreateProjectLogic from "./hooks/useCreateProjectLogic";
import { getProjectSetupOptions } from "./utils";
import { RootState } from "src/redux/reducers";

import "./CreateNewProjectPaneView.scss";

interface CreateNewProjectPaneViewProps {
  onClose: () => void;
  action: ProjectAction;
  projectId: string;
  briefPreview?: ReactNode;
}

export default function CreateNewProjectPaneView({
  onClose,
  action,
  projectId,
  briefPreview,
}: CreateNewProjectPaneViewProps) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const intl = useIntl();

  const [showPane, hidePane] = useShowPane(onClose);
  const [showWarningModal, setShowWarningModal] = useState(false);

  const {
    globalMetaFieldProjectWizardFields,
    isOpenAddBriefPane: isOpenAddBriefPaneForExistingProject,
  } = useSelector((state: RootState) => state.projectReducer);

  const { isOpenAddBriefPaneForNewProject } = useSelector(
    (state: RootState) => state.mainReducer,
  );

  const { formState, formValidation, formActions, formOptions } =
    useCreateProjectLogic(action, projectId);
  const projectSetupOptions = getProjectSetupOptions(intl);

  const isOpenAddBriefPane =
    action === "create"
      ? isOpenAddBriefPaneForNewProject
      : isOpenAddBriefPaneForExistingProject;

  const handleClosePane = () => {
    if (formValidation.hasUnsavedChanges) {
      setShowWarningModal(true);
    } else {
      hidePane();
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Enter" &&
        !formValidation.createCampaignButtonDisabled &&
        !isOpenAddBriefPane
      ) {
        buttonRef?.current?.click();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [formValidation.createCampaignButtonDisabled, isOpenAddBriefPane]);

  return (
    <>
      <Pane
        showPane={showPane}
        hidePane={handleClosePane}
        width={500}
        className="create-new-project-pane"
        backdropClassName="create-new-project-pane__backdrop"
      >
        <CreateNewProjectPaneHeader />
        <div className="pane__container">
          <div className="create-new-project-pane__form">
            <BriefSection briefPreview={briefPreview} />
            <GeneralSection
              coverImageFile={formState.coverImageFile}
              setNewImage={formActions.setNewImage}
              projectName={formState.projectName}
              onChangeProjectName={formActions.handleChangeProjectName}
              intl={intl}
            />
            <SettingsSection
              currentSetup={formState.currentProjectSetupOption}
              selectedCurrency={formState.selectedCurrencyOption}
              onCurrencyChange={formActions.setSelectedCurrencyOption}
              currencyOptions={formOptions.currencyOptions}
              intl={intl}
            />
            <StructureSection
              currentSetup={formState.currentProjectSetupOption}
              onChangeProjectSetup={formActions.setCurrentProjectSetupOption}
              intl={intl}
              projectSetupOptions={projectSetupOptions}
              selectedBaseTemplateOption={formState.selectedBaseTemplateOption}
              onChangeBaseTemplate={formActions.setSelectedBaseTemplateOption}
              selectedExistingProjectOption={
                formState.selectedExistingProjectOption
              }
              allBaseOptions={formOptions.allBaseOptions}
              onChangeExistingProject={
                formActions.setSelectedExistingProjectOption
              }
              selectedSocialPlatformTemplateOptions={
                formState.selectedSocialPlatformTemplateOptions
              }
              allSocialPlatformTemplateOptions={
                formOptions.allSocialPlatformTemplateOptions
              }
              onChangeSocialPlatformTemplate={
                formActions.setSelectedSocialPlatformTemplateOptions
              }
            />

            <WizardFieldsSection
              currentSetup={formState.currentProjectSetupOption}
              fields={globalMetaFieldProjectWizardFields}
            />
            <RequiredSection />
          </div>
        </div>
        <CreateNewProjectPaneFooter
          handleClosePane={handleClosePane}
          isCreatingProject={formState.isCreatingProject}
          submitProjectSetup={formActions.submitProjectSetup}
          createCampaignButtonDisabled={
            formValidation.createCampaignButtonDisabled
          }
          buttonRef={buttonRef}
        />
      </Pane>
      <CreateNewProjectPaneModals
        showWarningModal={showWarningModal}
        hidePane={hidePane}
        setShowWarningModal={setShowWarningModal}
      />
    </>
  );
}
